import {
  ResponsiveTableContext,
  makeController2,
  ResponsiveTable,
  Pagination,
  responsiveTableStore,
  responsiveTableActions,
  TableLoadingContext,
  defaultLabels,
} from '@apia/table';
import { action, makeObservable, observable, reaction, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useMemo } from 'react';
import { EventEmitter, TApiaFilter } from '@apia/util';
import { TableControllerEvents, TableControllerState } from './types';
import { Filter } from '../filters/Filter';

let controllerCount = 0;

export class TableController {
  private emitter = new EventEmitter<TableControllerEvents>();
  private tableName = `TableController${controllerCount++}`;
  private controllerComponent: FC = () => null;
  private selectionController: ReturnType<typeof makeController2>[0];

  state: TableControllerState = {
    finishedFirstLoad: false,
    columns: new Map(),
    filters: new Map(),
    isLoading: false,
    pagination: { currentPage: 0, totalPages: 0, totalRecords: 0 },
    props: {},
    rows: [],
  };

  // ids de filtros que se consideran "base" (creados en el init)
  private baseFilterIds: Set<string> = new Set();

  constructor(initialState?: Partial<TableControllerState>) {
    makeObservable(this, {
      state: observable,
      updateState: action,
    });

    const [selectionController, controllerComponent] = makeController2(
      this.tableName,
    );
    this.selectionController = selectionController;
    this.controllerComponent = controllerComponent;

    if (initialState) {
      Object.assign(this.state, initialState);
    }

    reaction(
      () => this.state.columns,
      (columns) => {
        responsiveTableStore.dispatch(
          responsiveTableActions.update({
            tableName: this.tableName,
            columns: [...toJS(columns).values()],
          }),
        );
      },
    );

    reaction(
      () => this.state.filters,
      () => {
        responsiveTableStore.dispatch(
          responsiveTableActions.update({
            tableName: this.tableName,
            filters: this.parseFilters(),
          }),
        );
      },
    );

    reaction(
      () => this.state.rows,
      (rows) => {
        responsiveTableStore.dispatch(
          responsiveTableActions.update({
            tableName: this.tableName,
            rows: toJS(rows),
          }),
        );
      },
    );
  }

  public clearFilters() {
    [...this.state.filters.values()].forEach((c) => (c.filterState.value = ''));
  }

  public clearSelection() {
    const tableState =
      responsiveTableStore.getState().responsiveTableSlice[this.tableName];

    if (!tableState?.selectedRows.length) {
      this.emitter.emit('onChangeSelection', []);
      return;
    }

    this.selectionController.setState({ selectedRows: [] });
  }

  public clearResults(showNoRegisters = true): void {
    this.clearSelection();
    this.updateState({
      finishedFirstLoad: showNoRegisters,
      pagination: { currentPage: 0, totalPages: 0, totalRecords: 0 },
      rows: [],
    });
  }

  private parseFilters() {
    return [...toJS(this.state.filters).values()].map((c) => {
      return {
        ...(c.filterState as any),
        id: c.filterState.id,
        column: c.filterState.isAdditional ? '' : c.filterState.column,
        asAdditional:
          c.filterState.isAdditional ||
          this.state.columns.get(c.filterState.column || '')?.hidden,
        options: c.filterState.options,
        detectOnChange: c.filterState.runAutomatically,
        runAutomatically: c.filterState.runAutomatically,
        title: c.filterState.title,
      } as TApiaFilter;
    });
  }

  public Context = observer(({ children }: { children: React.ReactNode }) => {
    return (
      <TableLoadingContext.Provider
        value={{ current: this.state.finishedFirstLoad }}
      >
        <this.TableContext>{children}</this.TableContext>
      </TableLoadingContext.Provider>
    );
  });

  private TableContext = observer(
    ({ children }: { children: React.ReactNode }) => {
      const state = this.state;
      const props = toJS(state.props);

      const columns = useMemo(
        () => [...toJS(state.columns).values()],
        [state.columns],
      );
      const rows = useMemo(() => [...toJS(state.rows).values()], [state.rows]);

      return (
        <ResponsiveTableContext
          {...props}
          columns={columns}
          rows={rows ?? []}
          sx={{ '.filterLabel': { display: 'none' } }}
          // eslint-disable-next-line react-hooks/exhaustive-deps
          filters={useMemo(() => this.parseFilters(), [this.state.filters])}
          FiltersRenderer={useCallback(
            (filterId: string) => this.state.filters.get(filterId)!.Component,
            [],
          )}
          customLabels={{
            noRegisters: this.state.finishedFirstLoad
              ? defaultLabels.noRegisters
              : '',
          }}
          name={this.tableName}
          SelectionHandler={this.controllerComponent}
          onSortChange={this.emitter.emit.bind(this.emitter, 'onSortChange')}
          onChangeSelection={this.emitter.emit.bind(
            this.emitter,
            'onChangeSelection',
          )}
          onFilterBlur={this.emitter.emit.bind(this.emitter, 'onFilterBlur')}
          onFilterChange={this.emitter.emit.bind(
            this.emitter,
            'onFilterChange',
          )}
          onFilterPressEnter={this.emitter.emit.bind(
            this.emitter,
            'onFilterPressEnter',
          )}
          onRowClick={this.emitter.emit.bind(this.emitter, 'onRowClick')}
          onSelectRows={this.emitter.emit.bind(this.emitter, 'onSelectRows')}
          isLoading={this.state.isLoading}
        >
          {children}
        </ResponsiveTableContext>
      );
    },
  );

  public getTableName() {
    return this.tableName;
  }

  public Pagination = observer(() => {
    return (
      <Pagination
        currentPage={this.state.pagination.currentPage}
        pageCount={this.state.pagination.totalPages}
        recordsCount={this.state.pagination.totalRecords}
        onPageChange={this.emitter.emit.bind(this.emitter, 'onPage')}
        onRefresh={this.emitter.emit.bind(this.emitter, 'onRefresh', null)}
        isLoading={this.state.isLoading}
        hideMaximizeButton
        listId={this.tableName}
      />
    );
  });

  public Table = ResponsiveTable;

  public on<K extends keyof TableControllerEvents>(
    ev: K,
    cb: (e: TableControllerEvents[K]) => unknown,
  ) {
    return this.emitter.on(ev, cb);
  }

  public updateFilters(newFilters: Filter[]) {
    newFilters.forEach((c) => {
      this.state.filters.set(c.filterState.id, c);
    });

    this.state.filters = new Map(this.state.filters);
  }

  public updateState(
    newState:
      | Partial<TableControllerState>
      | ((currentState: TableControllerState) => void),
  ) {
    if (newState instanceof Function) {
      newState(this.state);
    } else {
      Object.assign(this.state, newState);
    }
  }

  //Marca los filters actuales como los iniciales (llamar tras parseStructure/init).
  public markInitialFilters() {
    this.baseFilterIds = new Set([...this.state.filters.keys()].map(String));
  }

  // Elimina todos los filtros que NO son base (aquellos añadidos después del init).
  public removeAddedFilters() {
    for (const id of [...this.state.filters.keys()]) {
      if (!this.baseFilterIds.has(String(id))) {
        this.state.filters.delete(id);
      }
    }
    this.state.filters = new Map(this.state.filters);
  }

  public removeFilter(id: string) {
    this.state.filters.delete(id);
    this.state.filters = new Map(this.state.filters);
  }

  public clearFilter(id: string) {
    if (this.state.filters.has(id)) {
      this.state.filters.get(id)!.filterState.value = '';
      this.state.filters.get(id)!.filterState.filterToValue = '';
    }
  }
}
